"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Mail, Phone, MapPin, Calendar, Sparkles, Clock, CircleCheck as CheckCircle2, Send, Linkedin, MessageSquare } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const consultingPillars = [{
  id: 1,
  text: "OTA Revenue Management & Parity Alignment"
}, {
  id: 2,
  text: "Hospitality Tech Stack Audit & RMS Setup"
}, {
  id: 3,
  text: "Comprehensive Property Revenue Audits"
}];
function InquiryFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-xl border border-primary/40 bg-secondary/90 p-8 text-center shadow-lg">
        <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-primary/20 text-primary">
          <CheckCircle2 className="size-8" />
        </div>
        <h3 className="text-2xl font-bold text-secondary-foreground mb-2 font-serif">Inquiry Received</h3>
        <p className="text-secondary-foreground/80 mb-6 max-w-md mx-auto text-sm sm:text-base">
          {successMessage || "Thank you for reaching out. Sunil Singh will review your hotel details and get back to you shortly."}
        </p>
        <Button onClick={resetForm} variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField control={control} name="fullName" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary-foreground font-medium text-xs sm:text-sm">Your Name *</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Rajesh Kumar" className="bg-background/10 border-secondary-foreground/20 text-secondary-foreground placeholder:text-secondary-foreground/40 focus-visible:border-primary focus-visible:ring-primary/30" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary-foreground font-medium text-xs sm:text-sm">Email Address *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="e.g. owner@grandhotel.com" className="bg-background/10 border-secondary-foreground/20 text-secondary-foreground placeholder:text-secondary-foreground/40 focus-visible:border-primary focus-visible:ring-primary/30" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField control={control} name="hotelName" rules={{
        required: "Hotel or property name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary-foreground font-medium text-xs sm:text-sm">Hotel / Property Name *</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Ocean View Resort (45 Rooms)" className="bg-background/10 border-secondary-foreground/20 text-secondary-foreground placeholder:text-secondary-foreground/40 focus-visible:border-primary focus-visible:ring-primary/30" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary-foreground font-medium text-xs sm:text-sm">Phone Number *</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="e.g. +91 98765 43210" className="bg-background/10 border-secondary-foreground/20 text-secondary-foreground placeholder:text-secondary-foreground/40 focus-visible:border-primary focus-visible:ring-primary/30" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="consultingTopic" rules={{
      required: "Please select a service focus"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-secondary-foreground font-medium text-xs sm:text-sm">Primary Requirement *</FormLabel>
            <FormControl>
              <Input placeholder="e.g. OTA Strategy, Hospitality Tech, or Full Audit" className="bg-background/10 border-secondary-foreground/20 text-secondary-foreground placeholder:text-secondary-foreground/40 focus-visible:border-primary focus-visible:ring-primary/30" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please provide brief details about your property",
      minLength: {
        value: 10,
        message: "Minimum 10 characters required"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-secondary-foreground font-medium text-xs sm:text-sm">Property Details & Revenue Goals *</FormLabel>
            <FormControl>
              <Textarea placeholder="Share your current room count, PMS system, main channel challenges, or questions for Sunil..." className="bg-background/10 border-secondary-foreground/20 text-secondary-foreground placeholder:text-secondary-foreground/40 focus-visible:border-primary focus-visible:ring-primary/30 min-h-[110px]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/20 border border-destructive/40 rounded-md text-destructive-foreground text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base py-3 shadow-md mt-2">
        {isSubmitting ? <span className="flex items-center gap-2">
            <Clock className="animate-spin size-5" />
            Submitting Inquiry...
          </span> : <span className="flex items-center justify-center gap-2">
            <Send className="size-5" />
            Request Revenue Consultation
          </span>}
      </Button>
    </Form>;
}
export default function Inquiry() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="contactconsultinginquiry" className="bg-background text-foreground py-16 sm:py-24 px-4 sm:px-6 lg:px-8 transition-colors" data-nav="light">
      <div className="mx-auto max-w-7xl">
        <div className="bg-secondary text-secondary-foreground rounded-3xl p-6 sm:p-10 lg:p-12 border border-primary/30 shadow-2xl overflow-hidden relative">
          
          {}
          <div className="absolute -top-24 -right-24 size-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

          {}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center max-w-3xl mx-auto mb-12 sm:mb-16 relative z-10">
            <Badge variant="outline" className="border-primary/40 text-primary bg-primary/10 px-3 py-1 mb-4">
              <Sparkles className="size-3.5 mr-1" /> Direct Expert Consultation
            </Badge>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-serif tracking-tight text-secondary-foreground">
              Connect with Sunil Singh for Personalized Revenue Guidance
            </h2>

            <p className="mt-4 text-base sm:text-lg text-secondary-foreground/80 leading-relaxed">
              Have questions regarding OTA distribution, pricing strategies, or tech implementation? Get direct answers and data-backed solutions tailored to your hotel property.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start relative z-10">
            
            {}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="lg:col-span-5 space-y-6">
              {}
              <Card className="bg-background/5 border-secondary-foreground/15 text-secondary-foreground p-6 shadow-md">
                <div className="flex items-center gap-4">
                  <div className="size-16 rounded-full overflow-hidden border-2 border-primary/60 shrink-0 bg-secondary">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunil_singh_expert.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunil_singh_expert.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunil_singh_expert.webp-wvc-sizes" alt="Sunil Singh - Hotel Revenue Management Specialist" className="size-full object-cover" loading="lazy" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold font-serif text-secondary-foreground">Sunil Singh</h3>
                    <p className="text-sm text-primary font-medium">Hotel Revenue Management Specialist</p>
                    <p className="text-xs text-secondary-foreground/70 mt-0.5">Founder, HotelRevBaBa</p>
                  </div>
                </div>

                <Separator className="my-4 bg-secondary-foreground/15" />

                <div className="space-y-2 text-sm">
                  <p className="text-secondary-foreground/90 leading-relaxed">
                    {"\"Every hotel has unlocked revenue potential. I work directly with property owners and managers to clean up OTA distribution and optimize margins.\""}
                  </p>
                </div>

                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <Link to="https://linkedin.com" newTab className="inline-flex items-center gap-2 text-xs font-medium text-primary hover:underline">
                    <Linkedin className="size-4" /> Connect on LinkedIn
                  </Link>
                  <span className="text-secondary-foreground/30">•</span>
                  <Link to="/AboutSunilPage" className="text-xs font-medium text-secondary-foreground/80 hover:text-primary transition-colors">
                    Read Bio & Impact
                  </Link>
                </div>
              </Card>

              {}
              <div className="bg-background/5 rounded-2xl p-6 border border-secondary-foreground/15 space-y-3">
                <h4 className="text-sm font-semibold text-primary uppercase tracking-wider">Consulting Scope</h4>
                <div className="space-y-2">
                  {consultingPillars.map((pillar, i) => <div data-index={i} key={pillar.id} className="flex items-start gap-2.5 text-sm text-secondary-foreground/90">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>{pillar.text}</span>
                    </div>)}
                </div>
              </div>

              {}
              <div className="space-y-4 pt-2">
                <div data-index="0" className="flex items-start gap-4 p-4 rounded-xl bg-background/5 border border-secondary-foreground/10 hover:border-primary/40 transition-colors">
                  <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                    <Mail className="size-5" />
                  </div>
                  <div>
                    <h4 className="text-xs font-medium text-secondary-foreground/60 uppercase tracking-wider">Direct Email</h4>
                    <a href="mailto:sunil@hotelrevbaba.com" className="text-base font-semibold text-secondary-foreground hover:text-primary transition-colors block">
                      sunil@hotelrevbaba.com
                    </a>
                    <p className="text-xs text-secondary-foreground/70 mt-0.5">Expect a response within 12-24 hours</p>
                  </div>
                </div>

                <div data-index="1" className="flex items-start gap-4 p-4 rounded-xl bg-background/5 border border-secondary-foreground/10 hover:border-primary/40 transition-colors">
                  <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                    <Phone className="size-5" />
                  </div>
                  <div>
                    <h4 className="text-xs font-medium text-secondary-foreground/60 uppercase tracking-wider">Direct Call / WhatsApp</h4>
                    <a href="tel:+919876543210" className="text-base font-semibold text-secondary-foreground hover:text-primary transition-colors block">
                      +91 98765 43210
                    </a>
                    <p className="text-xs text-secondary-foreground/70 mt-0.5">Monday to Saturday, 9:00 AM - 7:00 PM IST</p>
                  </div>
                </div>

                <div data-index="2" className="flex items-start gap-4 p-4 rounded-xl bg-background/5 border border-secondary-foreground/10 hover:border-primary/40 transition-colors">
                  <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                    <MapPin className="size-5" />
                  </div>
                  <div>
                    <h4 className="text-xs font-medium text-secondary-foreground/60 uppercase tracking-wider">Consultancy Base</h4>
                    <p className="text-base font-semibold text-secondary-foreground">New Delhi, India</p>
                    <p className="text-xs text-secondary-foreground/70 mt-0.5">Available for global remote & on-site audits</p>
                  </div>
                </div>
              </div>

            </motion.div>

            {}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="lg:col-span-7">
              <Card className="bg-background/10 border-secondary-foreground/15 text-secondary-foreground p-6 sm:p-8 shadow-xl">
                <CardHeader className="p-0 mb-6">
                  <div className="flex items-center gap-2 mb-1">
                    <MessageSquare className="size-5 text-primary" />
                    <CardTitle className="text-2xl font-bold font-serif text-secondary-foreground">
                      Inquire About Your Property
                    </CardTitle>
                  </div>
                  <CardDescription className="text-secondary-foreground/70 text-sm">
                    Fill out the form below with your hotel details. Sunil will evaluate your requirements and reach out with strategic next steps.
                  </CardDescription>
                </CardHeader>

                <CardContent className="p-0">
                  <FormProvider formId="contact-consulting-inquiry" sectionName="ContactConsultingInquiry" defaultValues={{
                  fullName: "",
                  email: "",
                  hotelName: "",
                  phone: "",
                  consultingTopic: "",
                  message: ""
                }} validationRules={{
                  fullName: {
                    required: true,
                    minLength: 2
                  },
                  email: {
                    required: true,
                    pattern: "email"
                  },
                  hotelName: {
                    required: true
                  },
                  phone: {
                    required: true,
                    pattern: "phone"
                  },
                  consultingTopic: {
                    required: true
                  },
                  message: {
                    required: true,
                    minLength: 10
                  }
                }} submitText="Request Revenue Consultation" successMessage="Thank you! Your inquiry has been sent to Sunil Singh.">
                    <InquiryFormInner />
                  </FormProvider>
                </CardContent>
              </Card>

              {}
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between p-4 rounded-xl bg-primary/10 border border-primary/30 gap-4">
                <div className="flex items-center gap-3">
                  <SafeHydrate><Calendar className="size-6 text-primary shrink-0" /></SafeHydrate>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-secondary-foreground">Prefer a Direct Strategy Call?</p>
                    <p className="text-xs text-secondary-foreground/80">Schedule a 20-minute preliminary revenue audit call.</p>
                  </div>
                </div>
                <Button asChild variant="default" size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shrink-0">
                  <Link to="/ContactPage">
                    Book Strategy Call
                  </Link>
                </Button>
              </div>

            </motion.div>

          </div>

        </div>
      </div>
    </section>;
}